import {
  FmlxCheckBox,
  FmlxDatePicker,
  FmlxPagination,
  FmlxTableBody,
  FmlxTableCell,
  FmlxTableContainer,
  FmlxTableHead,
  FmlxTableHeadCell,
  FmlxTableRow,
  FmlxTextBox,
} from 'fmlx-common-ui';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CustomTableColumns,
  CustomTableProps,
  DateRangeFilter,
  ETableFilterTypes,
  FilterValue,
  TableSortInfo,
} from './constants';
import {
  getProcessedFilters,
  getValidFilterValues,
  getFilteredData,
  sortPreviewData,
} from './utils';
import { NodeDetailViewDateFilter } from 'components/customComponents/TreeControl/NodeDetailView/NodeDetailViewDateFilter';
import NoSearchResults from 'components/generic/noSearchResults';
import './styles.css';

export const CustomTable = (props: CustomTableProps) => {
  const {
    data,
    columns,
    id,
    rowsPerPage = 100,
    enableSelection = true,
    autoSelectAll = false,
    onCountsChange,
    sortInfo: defaultSortInfo = null,
  } = props;
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [sortInfo, setSortInfo] = useState<TableSortInfo>(defaultSortInfo);
  const [columnFilters, setColumnFilters] = useState<Record<string, FilterValue>>({});
  const [currentPage, setCurrentPage] = useState(0);

  // __srcIndex is the row's stable selection identity: the experiment id when the
  // caller provides one (previewTable/unarchivePreviewTable both do), falling back to
  // positional index otherwise. Keying off a stable id (rather than array position)
  // means a row's identity survives rows being added/removed elsewhere in the array.
  const tableData = useMemo(
    () => data.map((row, i) => ({ ...row, __srcIndex: row.__experimentId ?? i })),
    [data],
  );

  const previousIdsRef = useRef<Set<number> | null>(null);

  // Seed the selection with every row whenever a new dataset loads (e.g. after a path
  // selection previews experiments), so the table opens with all experiments selected.
  // User toggles afterwards persist until the next dataset arrives - except a *pure*
  // removal (e.g. a row disappearing because its experiment was deleted elsewhere,
  // detected live via a broadcast) isn't a new dataset: every id still present already
  // existed, so the user's selection is preserved as-is, just dropping the removed id(s).
  useEffect(() => {
    if (!enableSelection || !autoSelectAll) {
      return;
    }

    const currentIds = tableData.map((row) => row.__srcIndex);
    const currentIdSet = new Set(currentIds);
    const previousIds = previousIdsRef.current;
    previousIdsRef.current = currentIdSet;

    const isPureRemoval =
      previousIds !== null && currentIds.every((rowId) => previousIds.has(rowId));

    if (isPureRemoval) {
      setSelectedIds(
        (prev) => new Set([...prev].filter((rowId) => currentIdSet.has(rowId))),
      );
      return;
    }

    setSelectedIds(currentIdSet);
  }, [tableData, enableSelection, autoSelectAll]);

  const validFilters = useMemo(() => {
    const filters = getValidFilterValues(columnFilters);
    const processedFilters = getProcessedFilters(filters);
    return processedFilters;
  }, [columnFilters]);

  const filteredData = useMemo(() => {
    if (validFilters.length === 0) {
      return tableData;
    }
    return getFilteredData(tableData, validFilters);
  }, [tableData, validFilters]);

  const sortedData = useMemo(() => {
    if (!sortInfo) {
      return filteredData;
    }
    return sortPreviewData(filteredData, sortInfo, columns);
  }, [filteredData, sortInfo, columns]);

  // pagination: compute page data slice
  const pageData = useMemo(() => {
    const start = currentPage * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, currentPage, rowsPerPage]);

  // clamp currentPage when sortedData length or rowsPerPage changes
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(sortedData.length / rowsPerPage));
    const lastPageIndex = Math.max(0, totalPages - 1);
    if (currentPage > lastPageIndex) {
      setCurrentPage(lastPageIndex);
    }
  }, [sortedData.length, rowsPerPage, currentPage]);

  useEffect(() => {
    if (!enableSelection || !onCountsChange) {
      return;
    }

    const total = filteredData.length;
    const filteredIndices = new Set(filteredData.map((r) => r.__srcIndex));
    const selectedExperimentIds: number[] = [];
    let selected = 0;
    for (const id of selectedIds) {
      if (filteredIndices.has(id)) {
        selected++;
        const row = filteredData.find((filteredRow) => filteredRow.__srcIndex === id);
        if (row?.__experimentId != null) {
          selectedExperimentIds.push(row.__experimentId);
        }
      }
    }
    onCountsChange({ total, selected, selectedExperimentIds });
  }, [enableSelection, filteredData, selectedIds, onCountsChange]);

  const onSelectionChange = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const onSelectAllChange = () => {
    const visibleIds = sortedData.map((r) => r.__srcIndex);

    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = visibleIds.every((id) => next.has(id));
      if (allSelected) {
        for (const id of visibleIds) {
          next.delete(id);
        }
      } else {
        for (const id of visibleIds) {
          next.add(id);
        }
      }
      return next;
    });
  };

  const handleSortChange = (column: string) => {
    if (column === sortInfo?.column) {
      setSortInfo({ direction: sortInfo.direction === 'asc' ? 'desc' : 'asc', column });
    } else {
      setSortInfo({ direction: 'asc', column });
    }
  };

  const tableFilterRenderer = {
    [ETableFilterTypes.Date]: (key: string) => (
      <div className="fmlx-table-filter">
        <FmlxDatePicker
          variant={FmlxDatePicker.Variant.DATE}
          value={(columnFilters[key]?.value as string) ?? ''}
          onChange={(_event, value) =>
            handleFilterChange(key, { value, type: ETableFilterTypes.Date })
          }
        />
      </div>
    ),
    // DateRange filter kept for reuse; not currently attached to any column.
    [ETableFilterTypes.DateRange]: (key: string) => (
      <NodeDetailViewDateFilter
        startDate={(columnFilters[key]?.value as DateRangeFilter)?.start}
        endDate={(columnFilters[key]?.value as DateRangeFilter)?.end}
        onDateRangeChange={(start, end) =>
          handleFilterChange(key, {
            value: { start, end },
            type: ETableFilterTypes.DateRange,
          })
        }
      />
    ),
    [ETableFilterTypes.Text]: (key: string) => (
      <div className="fmlx-table-filter">
        <FmlxTextBox
          variant={FmlxTextBox.Variant.OUTLINE}
          mode={FmlxTextBox.Mode.TEXT}
          size={FmlxTextBox.Size.SMALL}
          value={columnFilters[key]?.value ?? ''}
          onChange={(e) =>
            handleFilterChange(key, { value: e.value, type: ETableFilterTypes.Text })
          }
        />
      </div>
    ),
  };

  // derive visible ids for header checkbox state (only current page)
  const visibleIds = useMemo(() => pageData.map((r) => r.__srcIndex), [pageData]);

  const headerAllSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const headerIndeterminate =
    visibleIds.some((id) => selectedIds.has(id)) && !headerAllSelected;

  const handleFilterChange = (key: string, value: FilterValue) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  const onPageChange = (event: React.ChangeEvent<HTMLInputElement>, page: number) => {
    setCurrentPage(page);
  };

  const showNoFilterResults =
    tableData.length > 0 && validFilters.length > 0 && filteredData.length === 0;
  const columnCount = (enableSelection ? 1 : 0) + columns.length;

  return (
    <div className="h-full w-full">
      <div className="h-[92%] w-full">
        <FmlxTableContainer
          classNames={{
            base: showNoFilterResults
              ? 'preview-table-base [&_table]:h-full [&_.MuiTableBody-root]:h-full'
              : 'preview-table-base',
            content: '',
          }}
          stickyHeader
        >
          <FmlxTableHead>
            <FmlxTableRow onClick={() => {}}>
              {enableSelection && (
                <FmlxTableHeadCell
                  variant="head"
                  align="left"
                  size="small"
                  label=""
                  sortDirection={null}
                  sortDisabled={true}
                  onClickSort={() => {}}
                  padding="checkbox"
                >
                  <FmlxCheckBox
                    checked={headerAllSelected}
                    indeterminate={headerIndeterminate}
                    onChange={onSelectAllChange}
                    labelPlacement="top"
                  />
                </FmlxTableHeadCell>
              )}
              {columns.map((column) => (
                <FmlxTableHeadCell
                  id={`${id}-header-${column.key}`}
                  key={`${id}-header-${column.key}`}
                  onClickSort={() => handleSortChange(column.key)}
                  align="left"
                  label={column.title}
                  sortDirection={
                    sortInfo?.column === column.key ? sortInfo.direction : null
                  }
                  variant={'head'}
                  size={'small'}
                  sortDisabled={column.disableSort ?? false}
                  className={column.className}
                >
                  <div className="pt-2">
                    {(() => {
                      const renderer = tableFilterRenderer[column.filterType];
                      return renderer ? renderer(column.key) : null;
                    })()}
                  </div>
                </FmlxTableHeadCell>
              ))}
            </FmlxTableRow>
          </FmlxTableHead>
          <FmlxTableBody>
            {showNoFilterResults ? (
              <tr className="h-full">
                <td colSpan={columnCount} className="h-full border-b-0 p-0 align-middle">
                  <NoSearchResults testId={`${id}-no-filter-results`} />
                </td>
              </tr>
            ) : (
              pageData.map((row, rowIndex) => (
                <TableRowMemo
                  key={`${id}-row-${currentPage}-${rowIndex}`}
                  rowIndex={rowIndex}
                  selectedIds={selectedIds}
                  row={row}
                  onSelectionChange={onSelectionChange}
                  columns={columns}
                  enableSelection={enableSelection}
                />
              ))
            )}
          </FmlxTableBody>
        </FmlxTableContainer>
      </div>
      <div className="h-[8%] w-full flex items-center">
        <FmlxPagination
          disabled={false}
          count={sortedData.length}
          variant="tablePagination"
          page={currentPage}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          showFirstLastButton
          showDisplaySettings
          className="fmlx-pagination w-full border-none"
        />
      </div>
    </div>
  );
};

type TableRowProps = {
  rowIndex: number;
  selectedIds: Set<number>;
  row: any;
  onSelectionChange: (e: number) => void;
  columns: CustomTableColumns[];
  enableSelection: boolean;
};

const TableRowMemo = React.memo((props: TableRowProps) => {
  const { rowIndex, selectedIds, row, onSelectionChange, columns, enableSelection } =
    props;
  return (
    <FmlxTableRow
      selected={enableSelection && selectedIds.has(row.__srcIndex)}
      onClick={enableSelection ? () => onSelectionChange(row.__srcIndex) : undefined}
    >
      {enableSelection && (
        <FmlxTableCell>
          <FmlxCheckBox checked={selectedIds.has(row.__srcIndex)} />
        </FmlxTableCell>
      )}
      {columns.map((column) => (
        <FmlxTableCell key={`preview-table-row-${rowIndex}-cell-${column.key}`}>
          {row[column.key]}
        </FmlxTableCell>
      ))}
    </FmlxTableRow>
  );
});
